import React, { useState, useEffect } from "react";
import {
  Card,
  Typography,
  Avatar,
  Button,
  Stack,
  TextField,
  Modal,
  Box,
  IconButton,
  CircularProgress,
  Rating,
  Alert,
  Snackbar,
  Tooltip,
  Menu,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import axios from "axios";

const CandidateCard = ({
  candidate,
  video,
  videoIds,
  currentVideoId,
  currentQuestionIndex,
  onQuestionChange,
  questions,
  image
}) => {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(candidate?.status);
  const [open, setOpen] = useState(false);
  const [link, setLink] = useState("");
  const [comment, setComment] = useState("");
  const [rating, setRating] = useState(0);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [severity, setSeverity] = useState("success");
  const [anchorEl, setAnchorEl] = useState(null);
  const [videos, setVideos] = useState([]);
  const [downloading, setDownloading] = useState(false);

  // Set videos from props when component mounts or candidate changes
  useEffect(() => {
    if (candidate?.media && candidate.media.length > 0) {
      setVideos(candidate.media);
    }
  }, [candidate]);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const downloadVideo = async (videoId, index) => {
    if (!videoId) {
      alert("No video available to download.");
      return;
    }

    setDownloading(true);
    handleMenuClose();

    try {
      // Construct the download URL using the pattern you requested
      const downloadUrl = `https://api.a2ajobs.com/media/${videoId}/download`;

      // Create a hidden link element and trigger the download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', `answer_${index + 1}.mp4`);
      link.setAttribute('target', '_blank');
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

    } catch (error) {
      console.error("Error downloading video:", error);
      alert("Failed to download video. It will open in a new tab instead.");

      // Fallback: Open in new tab
      window.open(`https://api.a2ajobs.com/media/${videoId}/download`, '_blank');
    }

    setDownloading(false);
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const generateLink = () => setLink("https://dummyinterview.com/share123");
  const deleteLink = () => setLink("");
  const showSnackbar = (msg, type) => {
    setMessage(msg);
    setSeverity(type);
    setOpenSnackbar(true);
  };

  // Update useEffect to depend on currentVideoId
  useEffect(() => {
    if (currentVideoId) {
      fetchReviews();
    }
  }, [currentVideoId, currentQuestionIndex]);

  const fetchReviews = async () => {
    setReviewsLoading(true);
    try {
      const response = await axios.get(
        `https://api.a2ajobs.com/api/reviews?candidate_id=${candidate?.id}&answer_id=${currentVideoId}`
      );
      if (response.status === 200 && response.data.data.length > 0) {
        const sortedReviews = response.data.data.sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );
        const latestReview = sortedReviews[0];
        setComment(latestReview.description || "");
        setRating(latestReview.review || 0);
      } else {
        setComment("");
        setRating(0);
      }
    } catch (error) {
      console.error("Error fetching reviews:", error);
      setComment("");
      setRating(0);
    }
    setReviewsLoading(false);
  };

  const updateCandidateStatus = async (newStatus) => {
    setLoading(true);
    try {
      const response = await axios.post(
        "https://api.a2ajobs.com/api/candidate-update",
        {
          candidate_id: candidate.id,
          status: newStatus,
        }
      );

      if (response.status === 200) {
        setStatus(newStatus);
      } else {
        console.error("Failed to update candidate");
      }
    } catch (error) {
      console.error("Error updating candidate:", error);
    }
    setLoading(false);
  };

  const handleSave = async () => {
    if (!comment.trim()) return;

    const payload = {
      candidate_id: candidate?.id,
      review: rating,
      description: comment,
      answer_id: currentVideoId || []
    };

    try {
      const response = await fetch(
        "https://api.a2ajobs.com/api/review-create",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (response.ok) {
        showSnackbar("Review saved successfully!", "success");
        fetchReviews();
      } else {
        showSnackbar("Failed to save review!", "error");
      }
    } catch (error) {
      console.error("Error saving review:", error);
      showSnackbar("Error saving review!", "error");
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => alert("Link copied to clipboard!"))
      .catch((err) => console.error("Failed to copy: ", err));
  };

  const cleanUrl = (url) => {
    if (!url) return null;
    return url.replace(/\\/g, '');
  };

  const baseURL =
    window.location.origin || "https://uiwebsite.schoolmgmtsys.com";

  return (
    <>
      <Card
        sx={{ p: { md: 2, xs: 1 }, mb: 2, width: { md: "100%", xs: "95%" } }}
      >
        <Typography variant="body2" color="textSecondary" sx={{ mt: 1, mb: 2 }}>
          Reviewing Question {currentQuestionIndex + 1} of {videoIds?.length || 0}
        </Typography>

        <Stack direction={"row"} gap={2}>
          <Avatar
            sx={{ width: 60, height: 60, mb: 2, fontSize: 15, color: "gray" }}
            src={cleanUrl(image)}
          >
            {!image && candidate?.name?.charAt(0)}
          </Avatar>
          <Stack>
            <Typography variant="h6" fontWeight={600}>
              {candidate?.name}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {candidate?.email}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {candidate?.phone}
            </Typography>
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{
                color:
                  status === "shortlisted"
                    ? "green"
                    : status === "discarded"
                      ? "red"
                      : "gray",
              }}
            >
              {status?.toUpperCase()}
            </Typography>
          </Stack>
        </Stack>

        <Stack direction={"row"} gap={2} mt={4}>
          <Button
            variant="contained"
            color="error"
            sx={{ mt: 2, width: "100%", textTransform: "none" }}
            onClick={() => updateCandidateStatus("discarded")}
            disabled={loading}
          >
            {loading && status === "discarded" ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Discard"
            )}
          </Button>

          <Button
            variant="contained"
            color="success"
            sx={{ mt: 2, width: "100%", textTransform: "none" }}
            onClick={() => updateCandidateStatus("shortlisted")}
            disabled={loading}
          >
            {loading && status === "shortlisted" ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Shortlist"
            )}
          </Button>
        </Stack>

        <Button
          variant="contained"
          color="primary"
          sx={{
            mt: 4,
            width: "100%",
            textTransform: "none",
            bgcolor: "#1666EB",
          }}
          onClick={handleOpen}
        >
          Share Candidate
        </Button>

        <Stack mt={4}>
          <Typography fontWeight={600}>Your review</Typography>
          {reviewsLoading ? (
            <CircularProgress size={24} sx={{ mt: 2 }} />
          ) : (
            <>
              <Rating
                value={rating}
                onChange={(event, newValue) => setRating(newValue)}
                sx={{ mb: 2 }}
              />
              <TextField
                label="Write your comments here..."
                fullWidth
                multiline
                value={comment}
                rows={2}
                onChange={(e) => setComment(e.target.value)}
                sx={{ mt: 2 }}
              />

              <Button
                variant="contained"
                color={"inherit"}
                onClick={handleSave}
                disabled={!comment.trim()}
                size="small"
                sx={{
                  mt: 2,
                  textTransform: "none",
                  alignSelf: "flex-end",
                  borderRadius: 20,
                }}
              >
                {"Save Comment"}
              </Button>
            </>
          )}
        </Stack>

        <Stack mt={4}>
          <Typography fontWeight={600}>Team reviews</Typography>
          <Typography variant="body2" color="text.secondary">
            No team reviews for this candidate yet
          </Typography>
        </Stack>

        {/* Download Videos Dropdown */}
        <Stack mt={4} textAlign={"start"}>
          <Button
            color="error"
            variant="contained"
            endIcon={<ArrowDropDownIcon />}
            onClick={handleMenuOpen}
            disabled={downloading}
            sx={{ textTransform: "none" }}
          >
            {downloading ? "Downloading..." : "Download Videos"}
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            {videos.length > 0 ? (
              videos.map((video, index) => (
                <MenuItem
                  key={video.id}
                  onClick={() => downloadVideo(video.id, index)}
                >
                  Answer {index + 1}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled>No videos available</MenuItem>
            )}
          </Menu>
        </Stack>

        <Modal open={open} onClose={handleClose}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: { md: "30%", xs: "80%" },
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 4,
              borderRadius: 2,
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              mb={1}
            >
              <Typography variant="h6" fontWeight={600}>
                Share Interview
              </Typography>
              <IconButton onClick={handleClose}>
                <CloseIcon />
              </IconButton>
            </Stack>
            <Typography variant="body2" color="textSecondary" mb={2}>
              Create a link that you can share with someone within your
              organization so they can view the interview.
            </Typography>
            {link ? (
              <Stack
                sx={{
                  border: "1px solid lightgrey",
                  p: 1,
                  borderRadius: "8px",
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Tooltip title="Copy" arrow>
                    <Typography
                      variant="body2"
                      color="primary"
                      flexGrow={1}
                      sx={{
                        textDecoration: "underline",
                        "&:hover": { cursor: "pointer" },
                      }}
                      onClick={() => {
                        const interviewLink = `${baseURL}/#/interviewDetails?id=${candidate?.id}`;
                        handleCopy(interviewLink);
                      }}
                    >
                      {`${baseURL}/#/interviewDetails`}
                    </Typography>
                  </Tooltip>
                  <IconButton onClick={deleteLink}>
                    <DeleteIcon />
                  </IconButton>
                </Stack>
                <Stack>
                  <Typography variant="caption" color="text.secondary">
                    Not for public sharing. Only share with individuals within
                    your organization.
                  </Typography>
                  <Typography variant="caption" color="body1" fontWeight={600}>
                    Link expires in 2 months
                  </Typography>
                </Stack>
              </Stack>
            ) : (
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={generateLink}
                sx={{ textTransform: "none" }}
              >
                Generate Link
              </Button>
            )}
          </Box>
        </Modal>
      </Card>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={3000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={severity}
          variant="filled"
        >
          {message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CandidateCard;